import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check } from '../skillUsage';

export const inlineHighlightChipSkill: CodeoSkill = {
    name: SKILL_NAMES.inlineHighlightChip,
    kind: 'codeo',
    description:
        'Wipe a solid block behind a phrase inside a line, knocking the text out of it — the ' +
        'typographic highlight, for the words that carry the claim.',
    checkLooksFor:
        'the chip wiped in on scaleX, and the text on it tweened on color (not backgroundColor) to read against the fill',
    documentUsesSkill: check((script) => /scaleX/.test(script) && /color:/.test(script)),
    instructions: `# Inline Highlight Chip

A solid block wipes in behind a phrase and the words on top of it invert to read against the fill. It is the crisp, typographic sibling of a marker stroke: where \`${SKILL_NAMES.markerEmphasis}\` reads as annotation on top of a page, this reads as the type having been *set* that way — a design decision rather than a reaction.

Use it on the phrase that carries the claim, once per graphic.

## Markup

The chip sits behind the phrase inside the flow, so the line's wrapping is unaffected.

\`\`\`html
<div class="absolute inset-0 flex items-center justify-center p-24">
  <div id="claim" style="position: relative; width: 1400px; font-family: var(--l5-font-0), sans-serif; font-size: 96px; font-weight: 700; line-height: 1.34; color: var(--l5-color-0, #101820);">
    Everything ships
    <span id="chip-wrap" class="inline-block" style="position: relative; padding: 4px 16px;">
      <span id="chip-fill" class="w-full h-full" style="position: absolute; left: 0; top: 0; background: var(--l5-color-2, #38bdf8); transform-origin: left center;"></span>
      <span id="chip-text" style="position: relative; color: var(--l5-color-0, #101820);">on the same day</span>
    </span>
  </div>
</div>
\`\`\`

## Timeline code

The text inverts partway through the wipe, so the knockout appears to be caused by the fill arriving under it.

\`\`\`js
const tl = gsap.timeline({ paused: true });
const MARK = 1.2;

gsap.set('#chip-fill', { scaleX: 0 });

tl.to('#chip-fill', { scaleX: 1, duration: 0.42, ease: 'power3.inOut' }, MARK);
tl.to('#chip-text', { color: 'var(--l5-color-1, #ffffff)', duration: 0.2, ease: 'none' }, MARK + 0.18);

window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Wipe direction**: left origin reads as writing. A \`center\` origin reads as the chip snapping into place, which is harder and suits a verdict.
- **Wipe speed**: 0.35–0.45s. Faster and the knockout mid-wipe is a flicker; slower and it reads as a loading bar.
- **Underline instead of fill**: scaling a short bar under the phrase rather than behind it is quieter and does not require the text to invert.
- **Rounded chip**: a pill radius reads as a tag or a label; square corners read as editorial. Pick to match the design language.
- **Two phrases**: possible across a longer line, staggered 0.6s apart, but the second always weakens the first.
- **Exit**: reversing the wipe returns the phrase to plain text and hands the frame on, which suits a graphic that continues past this beat.

## Pitfalls

- The fill is \`scaleX\`-tweened from a left origin and must not carry a CSS \`transform\` of its own.
- The text must sit above the fill in stacking order — \`position: relative\` on the text span is what does that, not a z-index guess.
- Invert the text *during* the wipe, not before it. Inverting early leaves the phrase unreadable against the page for a few frames.
- Check contrast in both states: the phrase has to be readable before the chip arrives and after it lands.
- Highlighting more than a few words turns the chip into a background and the emphasis into a paragraph.
- Don't use it on a line the narration is already emphasizing with a pulse — pick one emphasis technique per line.
- The chip changes the phrase's visual weight but not its box, so the line's wrapping is stable; keep the padding modest or it will push the wrap.`,
};
